import { Navigate, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { useAuth } from '../features/auth/hooks/useAuth';
import { useRoleSwitcher } from '../features/auth/hooks/useRoleSwitcher';
import { hasRouteAccess } from '../routes/routePermissions';
import { dashboardPathForRole } from '../routes/dashboardRouteContract';
import { useMenuItemsContext } from '../contexts/MenuItemsContext';
import { useSchool } from '../contexts/SchoolContext';

// Public routes accessible without authentication or role checks
const PUBLIC_ROUTES = ['/', '/roadmap', '/journey', '/login', '/login-join', '/register', '/forgot-password', '/welcome', '/join', '/join-bulk', '/parent-linking', '/setup-account', '/pricing', '/billing-success', '/buy/basic', '/buy/pro', '/buy/success', '/buy/cancel'];

/**
 * RoleBasedRoute - Checks if current role has permission to access the route
 * Shows AccessDenied page if access is denied
 *
 * This component checks on EVERY navigation:
 * - Role switches
 * - Direct URL typing
 * - Link clicks
 * - Browser back/forward
 */
export function RoleBasedRoute({ children }) {
  const location = useLocation();
  const { user, initializing } = useAuth();
  const { currentRole, loading, userPermissions } = useRoleSwitcher();
  const { allMenuItems, loading: menuLoading } = useMenuItemsContext();
  const { needsSchoolAssignment, loading: schoolLoading } = useSchool();
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname) || location.pathname.startsWith('/award/');
  const isLoading = initializing || loading || menuLoading || schoolLoading;

  // Memoize permission check at the top level (before any conditional returns)
  // This ensures hooks are called in consistent order
  const hasAccess = useMemo(
    () => {
      // Cached identity and menus can render before the role has been restored.
      // Check permissions only once the authenticated route is ready to use them.
      if (isPublicRoute || isLoading || !user) return false;
      return hasRouteAccess(location.pathname, currentRole, allMenuItems);
    },
    [location.pathname, currentRole, allMenuItems, isPublicRoute, isLoading, user]
  );

  // Skip all checks for public routes FIRST (before loading checks)
  if (isPublicRoute) {
    console.log('[RoleBasedRoute] Public route accessed:', location.pathname);
    return children;
  }

  // Once Firebase has resolved signed-out, school/menu/role reads are irrelevant
  // to this route. They must not trap the person behind a workspace loader.
  if (!initializing && !user) {
    return <Navigate to="/login" replace />;
  }

  // This guard is outside LoadingOrchestrator, so its children cannot display
  // a loading state until access has resolved. Keep genuine startup visible.
  if (isLoading) {
    return <div role="status" className="flex items-center justify-center gap-3 py-12 text-base-content/70">
      <span className="loading loading-spinner loading-sm" aria-hidden="true" />
      <span>Loading workspace…</span>
    </div>;
  }

  // Check if user needs to complete account setup
  // Skip setup check if already on setup page or on join flows
  const skipSetupCheck = location.pathname === '/setup-account' ||
                          location.pathname === '/join' ||
                          location.pathname === '/join-bulk' ||
                          location.pathname.startsWith('/parent');

  // Only redirect to setup if user has NO roles (brand new user)
  // Users with existing roles have already been set up, even if setupComplete is missing
  const hasRoles = userPermissions?.admin ||
                   userPermissions?.staff ||
                   userPermissions?.parent ||
                   userPermissions?.dev;

  const needsSetup = !skipSetupCheck &&
                     userPermissions &&
                     userPermissions.setupComplete === false &&
                     !hasRoles;

  console.log('[RoleBasedRoute] 🔑 Setup check:', {
    pathname: location.pathname,
    skipSetupCheck,
    hasUserPermissions: !!userPermissions,
    setupComplete: userPermissions?.setupComplete,
    hasRoles,
    needsSetup
  });

  if (needsSetup) {
    // Honor pending redirect over /setup-account — if a parent just signed up
    // and has a pending redirect to /parent-linking, send them there instead.
    const pendingRedirect = sessionStorage.getItem('pendingAuthRedirect');
    if (pendingRedirect) {
      console.log('[RoleBasedRoute] 🎯 needsSetup=true but pendingAuthRedirect found, honoring redirect:', pendingRedirect);
      sessionStorage.removeItem('pendingAuthRedirect');
      return <Navigate to={pendingRedirect} replace />;
    }
    console.log('[RoleBasedRoute] 🎯 User needs to complete setup - redirecting to /setup-account');
    return <Navigate to="/setup-account" replace />;
  }

  if (currentRole === 'teacher' && needsSchoolAssignment) {
    return <Navigate to="/join-bulk" replace />;
  }

  // Authenticated user → check role permissions
  if (!hasAccess) {
    console.log(`[RoleBasedRoute] Access denied - redirecting ${currentRole} to dashboard`);

    return <Navigate to={dashboardPathForRole(currentRole)} replace />;
  }

  return children;
}
